<script setup>
import { ref, computed, onMounted, inject } from 'vue';
import { Field, useField } from 'vee-validate';
import { vMaska } from 'maska/vue';
import {
	__,
	isNumber,
	clearDash,
	setFormValues,
	getFormValuesFromLocalStorage,
	decodeHTMLEntities,
	retrieveUrlParameter,
	requestPlanCTPbyID,
	handleNumberFieldPaste
} from '@/utils';
import nextStep from "@components/nextStep.vue";
import Accordion from "@components/Accordion.vue";
import AccordionItem from "@components/AccordionItem.vue";
import desiredNumber from './desiredNumber.vue';
import Confirmation from './addNumberConfirmation.vue';
import Plan from "./Plan.vue";

const { handleChange, value: formPhoneNumbers } = useField('phoneNumbers');
const defaultPhonesNumber = [{ number: '', confirmed: false, sim_type: '', plan: '', active: false, not_saved: true, ported: 0, plan_name: '', plan_price: ''  }]

const {
    availableMsisdns: cachedAvailableMsisdns,
} = inject('step1');
const { plans } = inject('step');

const savedFields = getFormValuesFromLocalStorage();

const hasGetParamID = retrieveUrlParameter('plan_id');

if (typeof mainPlan !== 'undefined' && mainPlan.plan_id && !hasGetParamID) {
	savedFields.mainPlanID = mainPlan.plan_id;
	setFormValues(savedFields);
}

if(hasGetParamID){
	savedFields.mainPlanID = hasGetParamID;
	setFormValues(savedFields);
}

const plan_id = savedFields?.mainPlanID;
const availbilityLoading = ref(false);
const isNotAvailable = ref(false);
const phoneNumbers = ref(formPhoneNumbers.value || defaultPhonesNumber);
const choosePlan = ref(false);
const showNewLineHead = ref(false);
const tempPlan = ref({});
const showAcceptNumber = ref(false);
  
const telecomData = computed(() => plans.value);

const handlePlanSelection = (plan) => {
	tempPlan.value = plan;
	showAcceptNumber.value = true;
};

const acceptNewLine = () => {
	addRow(tempPlan.value)
};

const addRow = (data) => {
	isNotAvailable.value = false;
	phoneNumbers.value.push({ number: '', confirmed: false, sim_type: '', plan: data.plan_id, active: false, not_saved: true, ported: 0, plan_name: data.title, plan_price: data?.montly_prices?.[0]?.price });
	choosePlan.value = false;
	tempPlan.value = {};
};

const addPlan = () => {
	choosePlan.value = true;
	showNewLineHead.value = true
	showAcceptNumber.value = false;
};

const addURLPlan = (index) => {
	if(!phoneNumbers.value[index].plan) {
		phoneNumbers.value[index].plan = plan_id;
	}
};

const cancleNumber = (index) => {
	if(phoneNumbers.value[index].not_saved) {
		phoneNumbers.value.splice(index, 1);
	} else {
		if(phoneNumbers.value[index].tempNumber) {
			phoneNumbers.value[index].number = phoneNumbers.value[index].tempNumber;
		}
		editNumber(index);
	}
	handleChange(phoneNumbers.value);
	if (phoneNumbers.value?.length === 0) {
		phoneNumbers.value = [{ number: '', confirmed: false, sim_type: '', plan: '', active: false }];
	}
};

const confirmationLogic = computed(() => phoneNumbers.value.some(item => !item.confirmed || item.sim_type?.length === 0));

const planIdsCheck = computed(() => phoneNumbers.value.some(item => item?.plan?.length === 0 || (typeof item.plan === 'undefined')));

const handleClickAcceptNumber = (data) => {
	const selectedNumber = data.computedNumber;
	const phone_id = cachedAvailableMsisdns.value?.data?.find(item => item.number.toString() === clearDash(selectedNumber))?.id;
	phoneNumbers.value[data.index].number = selectedNumber;
	phoneNumbers.value[data.index].confirmed = true;

	addURLPlan(data.index);

	if(phone_id) {
		phoneNumbers.value[data.index].phone_id = phone_id;
	}

	if(data?.phone_id) {
		phoneNumbers.value[data.index].phone_id = data.phone_id;
	}

	delete phoneNumbers.value[data.index].not_saved;
	if (phoneNumbers.value[data.index].plan === '') {
		const selectedPlan = localStorage.getItem('selected_main_plan') || ''; 
		const defaultPlan = selectedPlan || telecomData?.data[0].plan_id;
		phoneNumbers.value[data.index].plan = defaultPlan;
	}
	handleChange(phoneNumbers.value);
};

const handleSimType = (data) => {
	phoneNumbers.value[data.index].sim_type = data.sim;
	handleChange(phoneNumbers.value);
};

const handleInitialPlan = (data) => {
	phoneNumbers.value[data.index].plan = data.plan.plan_id;
	handleChange(phoneNumbers.value);
}

const deleteNumber = (index) => {
	isNotAvailable.value = false;
	phoneNumbers.value = phoneNumbers.value.filter((item, i) => i !== index);
	handleChange(phoneNumbers.value);
	if (phoneNumbers.value?.length === 0) {
		phoneNumbers.value = [{ number: '', confirmed: false, sim_type: '' }];
	}
};

const editNumber = (index) => {
	phoneNumbers.value[index].confirmed = !phoneNumbers.value[index].confirmed;
	if(!phoneNumbers.value[index].confirmed) {
		phoneNumbers.value[index].tempNumber = phoneNumbers.value[index].number.replace('-', '');
	}
};

const removeUnsaved = (index) => {
	delete phoneNumbers.value[index]?.not_saved;
}

const checkMsisdns = (selectedNumber, index) => {
	let number = selectedNumber;
	number = number.replace('-', '');
	availbilityLoading.value = true;

	const alreadySelectedNumber = phoneNumbers.value.some(phone => phone.number === selectedNumber && phone.confirmed);

	if(alreadySelectedNumber){
		isNotAvailable.value = true;
		availbilityLoading.value = false;
	}else{
		fetch(ajax_object.ajax_url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: `action=check_porting_number_availability&number=${number}`,
		})
		.then(response => response.json())
		.then(data => {
			delete phoneNumbers.value[index].not_saved;
			const apiResponse = JSON.parse(data.data);
			if (apiResponse.response.status) {
				phoneNumbers.value[index].confirmed = true;
				phoneNumbers.value[index].ported = 1;
				addURLPlan(index);
				handleChange(phoneNumbers.value);
			} else {
				isNotAvailable.value = true;
			}
		})
		.catch(error => console.error('Error:', error))
		.finally(() => {
			availbilityLoading.value = false;
		});
	}
};

const handleCancelPlan = () => {
	tempPlan.value = '';
	choosePlan.value = false;
}

const fetchPlanByID = async (id) => {
    try {
        const response = await requestPlanCTPbyID(id);
        phoneNumbers.value[0].plan_price = response?.data?.montly_prices?.[0]?.price;
		phoneNumbers.value[0].plan_name = response?.data?.title;
    } catch (error) {
        console.error('Error fetching plans:', error);
    }
};

onMounted(() => {
	if(plan_id && phoneNumbers.value.length){
		phoneNumbers.value[0].plan = plan_id;
		fetchPlanByID(plan_id)
	}
});

const selectedPlanDetails = (chosenPlan) => {
    const selectedPlan = plans.value?.data?.find(plan => plan.plan_id === chosenPlan);
    const price = selectedPlan?.custom_fields?.app_telecom_plan_montly_prices?.[0]?.price || 0;

    let message = '';

    if (selectedPlan?.title) {
        const hebrewInProgram = `\u200F${__('you have selected a plan')}\u200F`;
        const title = `\u200F${selectedPlan?.title}\u200F`;

        message += ` ${hebrewInProgram} ${title} - ${price} ₪`;
    }

    return message;
};
</script>
<template>
	<div class="form__body">
		<div class="form__row" v-for="(phone, index) in phoneNumbers">
			<label class="hidden" for="phone-1" v-if="!phone?.confirmed">
				{{ __('Type the main number that will be associated with the package yo.u have chosen') }}
			</label><!-- /.form__label -->

			<div class="plans plans__message-heading" v-if="!phone?.confirmed && index !== 0">
				<div class="plans__heading">
					<i class="ico ico--add-more"></i> {{ __('Add a line') }} {{ phoneNumbers.length }}:
					
				</div>
				<p v-if="phone?.plan?.length">
					{{ decodeHTMLEntities(selectedPlanDetails(phone.plan)) }}
				</p>
			</div><!-- /.plans -->

			<Accordion v-if="!phone?.confirmed">
				<AccordionItem :title="__('I want to keep my number')" :index="index">
					<div class="controls" v-if="!phone?.confirmed">
						<div class="controls__body">
							<div class="controls__field">
								<div class="field field--number"
									:class="{ 'field--has-error': phone.number.length > 0 && phone.number.length < 11 }">

									<label for="field-fname">
										{{ __('Enter a mobile number') }}
									</label>

									<Field type="text"
										:name="`phoneNumbers[${index}].number`"
										id="field-fname"
										v-maska
										data-maska="05#-##########"
										v-model="phone.number"
										@keypress="isNumber($event)"
										maxlength="11"
										@paste="handleNumberFieldPaste"
									/>

									<div class="field__error">
										{{ __('A valid phone number must be entered') }} ddd
									</div><!-- /.field__error -->
								</div><!-- /.field -->
							</div><!-- /.controls__field -->

							<div class="controls__actions">
								<button
									class="btn btn--arrows"
									type="button"
									@click.prevent="checkMsisdns(phone.number, index)"
									:disabled="phone.number.length != 11 || availbilityLoading"
								>
									<img :src="`${theme_path.url}/resources/images/spinner.svg`" alt="" v-if="availbilityLoading">
									{{ __('Continued') }}
								</button><!-- /.form__btn -->

								<button
									class="link-underline"
									type="button"
									@click.prevent="cancleNumber(index)"
									v-if="index > 0"
								>

									{{ __('Cancel') }}
								</button><!-- /.link-arrow -->
							</div><!-- /.controls__actions -->
						</div><!-- /.controls__body -->
						<div class="controls__foot" v-if="isNotAvailable">
							<div class="controls__error">
								{{ __('This number cannot be selected') }}
							</div>
						</div><!-- /.controls__foot -->
					</div><!-- /.controls -->
				</AccordionItem>

				<AccordionItem :title="__('I want a new number')" :index="index+999">
					<desiredNumber
						@clickAcceptNumber="handleClickAcceptNumber"
						@cancelNumber="cancleNumber"
						@removeUnsaved="removeUnsaved"
						:index="index"
					/>
				</AccordionItem>
			</Accordion>

			<Confirmation
				:number="phone"
				:index="index"
				@deleteNumber="deleteNumber"
				@editNumber="editNumber"
				@simType="handleSimType"
				@selectedPlan="handleInitialPlan"
			/>
		</div><!-- /.form__row -->

		<div class="cta-text" v-if="!confirmationLogic && !choosePlan">
			<i class="ico ico--add-more"></i>

			<h6>
				{{ __('Want to add another line?') }}
			</h6>

			<button class="link-arrows" type="button" @click="addPlan">
				{{ __('Click Here') }}
			</button><!-- /.link-arrow -->
		</div><!-- /.cta-text -->

		<div class="plans" v-if="choosePlan">
			<div class="plans__heading">
				<i class="ico ico--add-more"></i> {{ __('Add a line') }} {{ phoneNumbers.length + 1 }}:
			</div>
			<div class="plans__subheading">
				בחר תוכנית
			</div>
			<div class="plans-container" v-if="telecomData?.data">
				<Plan
					v-for="(plan, planIndex) in telecomData.data "
					:index="planIndex"
					:plan="plan" 
					:selected="tempPlan?.plan_id === plan.plan_id"
					@numberPlan="handlePlanSelection"
				/>

				<button class="btn btn--arrows align-left" type="button" @click="acceptNewLine" v-if="showAcceptNumber">
					{{ __('Continue') }}
				</button><!-- /.link-arrow -->
			</div>
		</div><!-- /.plans -->
	</div><!-- /.form__body -->

	<nextStep v-if="!confirmationLogic && !choosePlan && !planIdsCheck && phoneNumbers.length" />
</template>
